/* Quick Pro USA — Zendesk chat launcher: position math + runtime.
 * Pure math lives on window.QPUChatPosition so tests/gate can eval this
 * file in jsdom without a live DOM (tests/gate/chat-position.test.mjs).
 * Runtime behavior is covered by tests/integration/chat-launcher-dom.test.mjs
 * and tests/playwright/chat-launcher.spec.mjs.
 */
(function () {
  "use strict";

  var OBSTRUCTION_GAP = 12;
  var TOP_RESERVE = 96;
  var NUDGE_STEP = 16;
  // Natural click jitter on mice/trackpads runs 4-10px; anything below this
  // must stay a click or the launcher reads as a dead button.
  var DRAG_THRESHOLD = 12;
  var STORAGE_KEY = "qpu-chat-pos:v1";

  var position = {
    snapSide: function (centerX, viewportW) {
      return centerX > viewportW / 2 ? "right" : "left";
    },
    minBottom: function (obstructionPx, baseInset) {
      return obstructionPx > 0 ? obstructionPx + OBSTRUCTION_GAP : baseInset;
    },
    clampBottom: function (bottomPx, viewportH, launcherH, minBottomPx) {
      var max = viewportH - TOP_RESERVE - launcherH;
      if (max < minBottomPx) max = minBottomPx;
      return Math.min(Math.max(bottomPx, minBottomPx), max);
    },
    serialize: function (side, bottomPx, viewportH) {
      return JSON.stringify({
        side: side,
        bottomPct: Math.round((bottomPx / viewportH) * 10000) / 100
      });
    },
    deserialize: function (raw, viewportH) {
      var data;
      try { data = JSON.parse(raw); } catch (e) { return null; }
      if (!data || (data.side !== "left" && data.side !== "right")) return null;
      var pct = data.bottomPct;
      if (typeof pct !== "number" || !isFinite(pct) || pct < 0 || pct > 100) return null;
      return { side: data.side, bottomPx: Math.round((pct / 100) * viewportH) };
    },
    isDrag: function (dx, dy) {
      return Math.hypot(dx, dy) >= DRAG_THRESHOLD;
    }
  };
  window.QPUChatPosition = position;

  function init() {
    var openBtn = document.getElementById("qpu-launcher");
    var closeBtn = document.getElementById("qpu-close");
    var brandOverlay = document.getElementById("qpu-brand-overlay");
    var liveRegion = document.getElementById("qpu-chat-live");
    if (!openBtn || !closeBtn || !brandOverlay) return;

    var state = { side: "left", bottomPx: baseInset() };
    var effectiveBottom = state.bottomPx;
    var dragging = false;
    var suppressClick = false;
    var startX = 0;
    var startY = 0;

    function baseInset() {
      return window.innerWidth <= 480 ? 16 : 24;
    }

    function launcherHeight() {
      return openBtn.offsetHeight || 44;
    }

    function stickyObstruction() {
      if (!document.documentElement.classList.contains("stick-atc-show")) return 0;
      var bar = document.querySelector(".m-sticky-addtocart");
      return bar ? bar.offsetHeight : 0;
    }

    function previewBarObstruction() {
      // Shopify's theme-preview bar (only in ?preview_theme_id sessions)
      // occupies the bottom band and swallows clicks; rest above it so
      // preview reviewers can use the launcher. Never present on the
      // published theme.
      return document.getElementById("PBarNextFrameWrapper") ? 56 : 0;
    }

    function bottomObstruction() {
      return Math.max(stickyObstruction(), previewBarObstruction());
    }

    function readStored() {
      try {
        return position.deserialize(localStorage.getItem(STORAGE_KEY), window.innerHeight);
      } catch (e) { return null; }
    }

    function persist() {
      try {
        localStorage.setItem(STORAGE_KEY, position.serialize(state.side, state.bottomPx, window.innerHeight));
      } catch (e) { /* storage blocked: default position next visit */ }
    }

    function announce(msg) {
      if (liveRegion) liveRegion.textContent = msg;
    }

    function apply() {
      var floor = position.minBottom(bottomObstruction(), baseInset());
      effectiveBottom = position.clampBottom(state.bottomPx, window.innerHeight, launcherHeight(), floor);
      document.documentElement.style.setProperty("--qpu-chat-bottom", effectiveBottom + "px");
      openBtn.classList.toggle("qpu-chat--right", state.side === "right");
      closeBtn.classList.toggle("qpu-chat--right", state.side === "right");
      constrainSize();
    }

    // ---- drag (pointer events; below-threshold movement stays a click) ----
    openBtn.addEventListener("pointerdown", function (e) {
      if (e.pointerType === "mouse" && e.button !== 0) return;
      startX = e.clientX;
      startY = e.clientY;
      dragging = false;
      if (openBtn.setPointerCapture) openBtn.setPointerCapture(e.pointerId);
    });

    openBtn.addEventListener("pointermove", function (e) {
      if (!openBtn.hasPointerCapture || !openBtn.hasPointerCapture(e.pointerId)) return;
      if (!dragging) {
        if (!position.isDrag(e.clientX - startX, e.clientY - startY)) return;
        dragging = true;
        openBtn.classList.add("qpu-chat--dragging");
      }
      openBtn.style.left = (e.clientX - openBtn.offsetWidth / 2) + "px";
      openBtn.style.top = (e.clientY - openBtn.offsetHeight / 2) + "px";
      openBtn.style.right = "auto";
      openBtn.style.bottom = "auto";
    });

    function endDrag() {
      openBtn.classList.remove("qpu-chat--dragging");
      openBtn.style.left = "";
      openBtn.style.top = "";
      openBtn.style.right = "";
      openBtn.style.bottom = "";
    }

    openBtn.addEventListener("pointerup", function (e) {
      if (openBtn.hasPointerCapture && openBtn.hasPointerCapture(e.pointerId)) {
        openBtn.releasePointerCapture(e.pointerId);
      }
      if (!dragging) return;
      dragging = false;
      suppressClick = true;
      setTimeout(function () { suppressClick = false; }, 0);
      var rect = openBtn.getBoundingClientRect();
      state.side = position.snapSide(rect.left + rect.width / 2, window.innerWidth);
      state.bottomPx = window.innerHeight - rect.bottom;
      endDrag();
      apply();
      persist();
      announce("Chat button moved to " + state.side + " edge");
    });

    openBtn.addEventListener("pointercancel", function () {
      dragging = false;
      endDrag();
      apply();
    });

    // ---- keyboard repositioning (launcher focused) ----
    openBtn.addEventListener("keydown", function (e) {
      var msg = null;
      if (e.key === "ArrowLeft") { state.side = "left"; msg = "Chat button moved to left edge"; }
      else if (e.key === "ArrowRight") { state.side = "right"; msg = "Chat button moved to right edge"; }
      else if (e.key === "ArrowUp") { state.bottomPx = effectiveBottom + NUDGE_STEP; msg = "Chat button moved up"; }
      else if (e.key === "ArrowDown") { state.bottomPx = effectiveBottom - NUDGE_STEP; msg = "Chat button moved down"; }
      if (!msg) return;
      e.preventDefault();
      var floor = position.minBottom(bottomObstruction(), baseInset());
      state.bottomPx = position.clampBottom(state.bottomPx, window.innerHeight, launcherHeight(), floor);
      apply();
      persist();
      announce(msg);
    });

    // ---- open/close (carried over from the previous theme.liquid inline code) ----
    function updateBrandOverlay() {
      var chatFrame = null;
      document.querySelectorAll("iframe").forEach(function (f) {
        if (f.id === "launcher") return;
        var r = f.getBoundingClientRect();
        if (r.height > 200) chatFrame = f;
      });
      if (!chatFrame) return;
      var rect = chatFrame.getBoundingClientRect();
      brandOverlay.style.left = rect.left + "px";
      brandOverlay.style.top = (rect.bottom - 36) + "px";
      brandOverlay.style.width = rect.width + "px";
      brandOverlay.style.height = "36px";
    }

    function openWidget() {
      openBtn.classList.add("is-open");
      closeBtn.classList.add("visible");
      if (typeof zE === "function") {
        try { zE("messenger", "open"); }
        catch (e) { try { zE("webWidget", "open"); } catch (_) {} }
      }
      setTimeout(function () {
        updateBrandOverlay();
        brandOverlay.classList.add("visible");
      }, 400);
    }

    function closeWidget() {
      openBtn.classList.remove("is-open");
      closeBtn.classList.remove("visible");
      brandOverlay.classList.remove("visible");
      if (typeof zE === "function") {
        try { zE("messenger", "close"); }
        catch (e) { try { zE("webWidget", "close"); } catch (_) {} }
      }
    }

    openBtn.addEventListener("click", function (e) {
      if (suppressClick) {
        suppressClick = false;
        e.preventDefault();
        e.stopImmediatePropagation();
        return;
      }
      openWidget();
    });
    closeBtn.addEventListener("click", closeWidget);

    function onZendeskClose() {
      openBtn.classList.remove("is-open");
      closeBtn.classList.remove("visible");
      brandOverlay.classList.remove("visible");
    }

    function registerCloseHandlers() {
      if (typeof zE !== "function") return;
      try { zE("messenger:on", "close", onZendeskClose); } catch (e) {}
      try { zE("webWidget:on", "close", onZendeskClose); } catch (e) {}
    }
    registerCloseHandlers();
    window.addEventListener("load", registerCloseHandlers);

    // ---- anchor the open chat iframe to the launcher's side ----
    function constrainSize() {
      var windowBottom = (effectiveBottom + launcherHeight() + OBSTRUCTION_GAP) + "px";
      var inset = (window.innerWidth <= 640 ? 16 : 24) + "px";
      document.querySelectorAll("iframe").forEach(function (f) {
        if (f.id === "launcher") return;
        var rect = f.getBoundingClientRect();
        if (rect.height < 200) return;
        if (window.innerWidth <= 640) {
          f.style.setProperty("height", "65vh", "important");
          f.style.setProperty("max-height", "520px", "important");
          f.style.setProperty("width", "calc(100vw - 32px)", "important");
        } else {
          f.style.setProperty("height", "450px", "important");
          f.style.setProperty("max-height", "450px", "important");
          f.style.setProperty("width", "370px", "important");
        }
        f.style.setProperty("bottom", windowBottom, "important");
        f.style.setProperty("top", "auto", "important");
        f.style.setProperty("border-radius", "16px", "important");
        if (state.side === "left") {
          f.style.setProperty("left", inset, "important");
          f.style.setProperty("right", "auto", "important");
        } else {
          f.style.setProperty("right", inset, "important");
          f.style.setProperty("left", "auto", "important");
        }
      });
    }

    // ---- hide while the cart drawer is open (never cover Checkout) ----
    var drawer = document.getElementById("MinimogCartDrawer");
    if (drawer) {
      new MutationObserver(function () {
        var open = drawer.classList.contains("m-cart-drawer--active");
        openBtn.classList.toggle("qpu-chat--drawer-open", open);
        closeBtn.classList.toggle("qpu-chat--drawer-open", open);
        // Also hides Zendesk's proactive-message bubbles via CSS :has() —
        // they float at z-index 999999 and can cover the drawer's Checkout.
        document.documentElement.classList.toggle("qpu-drawer-open", open);
      }).observe(drawer, { attributes: true, attributeFilter: ["class"] });
    }

    // ---- keep clear of the sticky ATC bar; track iframe restyles ----
    var rafPending = false;
    function onScrollResize() {
      if (rafPending) return;
      rafPending = true;
      requestAnimationFrame(function () {
        rafPending = false;
        apply();
        if (brandOverlay.classList.contains("visible")) updateBrandOverlay();
      });
    }
    window.addEventListener("scroll", onScrollResize, { passive: true });
    window.addEventListener("resize", onScrollResize);

    // This observer's callbacks WRITE styles into the subtree it observes.
    // It does not loop only because CSSOM setProperty short-circuits when
    // value+priority are unchanged and both writers are idempotent for a
    // fixed state. Keep any new writes idempotent or this loops.
    new MutationObserver(function () {
      constrainSize();
      if (brandOverlay.classList.contains("visible")) updateBrandOverlay();
    }).observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ["style"]
    });

    // ---- boot ----
    var stored = readStored();
    if (stored) {
      state.side = stored.side;
      state.bottomPx = stored.bottomPx;
    }
    apply();
  }

  /* ---- global [data-qp-open-chat] fallback delegate --------------------
     Regression fix (PM report 2026-07-12): several chat CTAs were DEAD
     because every delegate was scope-locked and nobody owned the rest —
       · qp-nav.js       -> only buttons inside the nav sheet / drawers
       · qp-shipping.js  -> only buttons inside [data-qp-shipping-shell]
                            (the modal — the shipping PAGE's own two
                            buttons never matched)
       · cart-template   -> page-wide, but only exists on /cart (which is
                            also why the footer button "worked" there and
                            nowhere else)
     The chat runtime itself (this file, loaded globally with the
     launcher via snippets/zendesk-chat.liquid) now owns the fallback:
     any [data-qp-open-chat] click outside the three scoped regions
     opens the launcher.

     CAPTURE phase + stopPropagation, with the scoped regions SKIPPED:
     capture-with-stop is how the peers already guarantee single-fire —
     stopping here (document, capture) prevents the event from ever
     reaching target/bubble listeners, so the cart page's page-wide
     bubble delegate cannot double-open for the buttons we handle (a
     second click toggles the Tidio fallback closed). The skip keeps the
     scoped owners authoritative for their own regions: same-node
     capture listeners all run regardless of stopPropagation order, so
     without it a drawer chat click would fire both qp-nav.js's handler
     and this one. */
  document.addEventListener(
    "click",
    function (e) {
      if (!e.target || !e.target.closest) return;
      var btn = e.target.closest("[data-qp-open-chat]");
      if (!btn) return;
      if (
        btn.closest(
          "[data-qp-nav-sheet], [data-qp-drawer], [data-qp-shipping-shell]"
        )
      ) {
        return; /* a scoped delegate owns this region */
      }
      e.stopPropagation();
      var launcher =
        document.querySelector("#qpu-launcher") ||
        document.querySelector("#tidio-chat iframe, #tidio-chat-iframe");
      if (launcher && typeof launcher.click === "function") launcher.click();
    },
    true
  );

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();
